import type { ContractedBook } from '@/data/types';

export const LYDBOK_KVALIFISERING_DAGER = 365;
export const LYDBOK_KVALIFISERING_SALG = 5_000;
export const LYDBOK_TILBUD_SJANSE = 0.08;
export const LYDBOK_ROYALTY_VARIGHET = 180;
export const LYDBOK_BASE_FORSKUDD = 30_000;
export const LYDBOK_MAX_FORSKUDD = 150_000;
export const LYDBOK_FORHANDLE_SJANSE = 0.5;
export const LYDBOK_FORHANDLE_BONUS = 0.3;
export const LYDBOK_DAGLIG_ROYALTY_ANDEL = 0.05;

export const FILM_KVALIFISERING_SALG = 25_000;
export const FILM_KVALIFISERING_ANMELDELSE_SNITT = 4.5;
export const FILM_TILBUD_SJANSE = 0.03;
export const FILM_BASE_OPSJON = 100_000;
export const FILM_MAX_OPSJON = 500_000;
export const FILM_PRODUKSJON_MIN_DAGER = 90;
export const FILM_PRODUKSJON_MAX_DAGER = 180;
export const FILM_LANSERING_SJANSE = 0.35;
export const FILM_LANSERING_BONUS = 200_000;
export const FILM_LANSERING_BOOST_FAKTOR = 3.0;
export const FILM_LANSERING_BOOST_DAGER = 90;
export const FILM_LANSERING_OMDOMME = 20;
export const FILM_IKKE_PRODUSERT_OMDOMME = -2;
export const LYDBOK_FORHANDLE_TAP_OMDOMME = -2;

export const LYDBOK_TOP_BORDER_HEX = 0x3d5a4d;
export const FILM_TOP_BORDER_HEX = 0x4a3a5e;

export type LydbokAvtale = {
  isbn: string;
  bokTittel: string;
  tjenesteId: string;
  tjenesteNavn: string;
  startDag: number;
  ferdigDag: number;
  engangsBelop: number;
  dagligRoyalty: number;
};

export type FilmProduseresStatus =
  | 'venter'
  | 'produseres'
  | 'lansert'
  | 'ikke_produsert';

export type FilmRettighet = {
  isbn: string;
  bokTittel: string;
  studioId: string;
  studioNavn: string;
  startDag: number;
  opsjonsBelop: number;
  produseresStatus: FilmProduseresStatus;
  produseresFerdigDag: number | null;
};

export type PlanlagtFilmProduksjon = {
  isbn: string;
  studioId: string;
  ferdigDag: number;
};

export function eventIdLydbokTilbud(isbn: string, dag: number): string {
  return `synth_lydbok_tilbud_${isbn}_${dag}`;
}

export function eventIdFilmTilbud(isbn: string, dag: number): string {
  return `synth_film_tilbud_${isbn}_${dag}`;
}

export function snittTerninger(bok: ContractedBook): number {
  if (bok.anmeldelser.length === 0) return 0;
  let sum = 0;
  for (const a of bok.anmeldelser) sum += a.terninger;
  return sum / bok.anmeldelser.length;
}

export function erKvalifisertForLydbok(
  bok: ContractedBook,
  dag: number,
  eksisterende: ReadonlyArray<LydbokAvtale>,
): boolean {
  if (bok.status !== 'utgitt') return false;
  if (bok.utgivelsesDag === null) return false;
  const dagerSiden = dag - bok.utgivelsesDag;
  if (dagerSiden < LYDBOK_KVALIFISERING_DAGER) return false;
  if (bok.salg < LYDBOK_KVALIFISERING_SALG) return false;
  if (eksisterende.some((a) => a.isbn === bok.isbn && dag <= a.ferdigDag)) {
    return false;
  }
  return true;
}

export function erKvalifisertForFilm(
  bok: ContractedBook,
  eksisterende: ReadonlyArray<FilmRettighet>,
): boolean {
  if (bok.status !== 'utgitt') return false;
  if (bok.salg < FILM_KVALIFISERING_SALG) return false;
  const snitt = snittTerninger(bok);
  if (snitt < FILM_KVALIFISERING_ANMELDELSE_SNITT) return false;
  if (eksisterende.some((r) => r.isbn === bok.isbn)) return false;
  return true;
}

export function beregnLydbokForskudd(
  bok: ContractedBook,
  rng: () => number,
): number {
  const tierMult: Record<string, number> = { D: 1.0, C: 1.2, B: 1.4, A: 1.7, S: 2.0 };
  const mult = tierMult[bok.tier] ?? 1.0;
  const salgsFaktor = Math.min(3.0, 1.0 + bok.salg / 50_000);
  const base = LYDBOK_BASE_FORSKUDD;
  const tak = LYDBOK_MAX_FORSKUDD;
  const jitter = 0.8 + rng() * 0.4;
  const raw = base * mult * salgsFaktor * jitter;
  const klemt = Math.max(LYDBOK_BASE_FORSKUDD, Math.min(tak, raw));
  return Math.round(klemt / 1000) * 1000;
}

export function beregnLydbokDagligRoyalty(forskudd: number): number {
  const royalty = Math.round((forskudd * LYDBOK_DAGLIG_ROYALTY_ANDEL) / LYDBOK_ROYALTY_VARIGHET);
  return Math.max(10, royalty);
}

export function beregnFilmOpsjon(
  bok: ContractedBook,
  rng: () => number,
): number {
  const snitt = snittTerninger(bok);
  const tierMult: Record<string, number> = { D: 1.0, C: 1.3, B: 1.6, A: 2.0, S: 2.5 };
  const mult = tierMult[bok.tier] ?? 1.0;
  const salgsFaktor = Math.min(2.5, 1.0 + bok.salg / 100_000);
  const anmFaktor = Math.max(1.0, snitt / 4.5);
  const jitter = 0.85 + rng() * 0.3;
  const raw = FILM_BASE_OPSJON * mult * salgsFaktor * anmFaktor * jitter;
  const klemt = Math.max(FILM_BASE_OPSJON, Math.min(FILM_MAX_OPSJON, raw));
  return Math.round(klemt / 5000) * 5000;
}

export function beregnFilmProduksjonsDager(rng: () => number): number {
  const spread = FILM_PRODUKSJON_MAX_DAGER - FILM_PRODUKSJON_MIN_DAGER;
  return FILM_PRODUKSJON_MIN_DAGER + Math.floor(rng() * (spread + 1));
}

export function harLydbokAvtaler(
  avtaler: ReadonlyArray<LydbokAvtale>,
  minst: number,
): boolean {
  return avtaler.length >= minst;
}

export function harFilmRettigheter(
  rettigheter: ReadonlyArray<FilmRettighet>,
  minst: number,
): boolean {
  return rettigheter.length >= minst;
}
